import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import cn from "../../../utils/classes"
import { Typography, typographyTypes } from "./Typography"
import Icon from "./Icon"

export interface DrawerProps {
  /** Controls the visibility of the drawer. */
  isOpen: boolean
  /** The title of the drawer. */
  title?: string
  /** Specifies from which side the drawer will slide in. */
  position?: "left" | "right" | "top" | "bottom"
  /** Function to call when the drawer should be closed. */
  onClose?: () => void
  /** Optional ID for the drawer. */
  id?: string
  /** Width of the drawer. */
  width?: string
  /** Height of the drawer. */
  height?: string
  /** zIndex of the drawer. */
  zIndex?: number
  /** Opacity of the backdrop. */
  backdropOpacity?: number
  /** Whether the drawer should close when clicking on the backdrop. */
  closeOnBackdropClick?: boolean
  /** Style overrides for the Drawer's container div element. */
  containerStyleOverrides?: string
  /** Style overrides for the Drawer's title div element. */
  titleStyleOverrides?: string
}

const Drawer: React.FC<React.PropsWithChildren<DrawerProps>> = ({
  isOpen,
  title,
  position = "left",
  onClose,
  children,
  id,
  width,
  height,
  zIndex = 15,
  backdropOpacity,
  closeOnBackdropClick = false,
  containerStyleOverrides,
  titleStyleOverrides,
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(isOpen)

  const drawerId =
    id ||
    `drawer-title-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`
  const drawerRef = useRef<HTMLDivElement>(null)

  const containerClassName = useMemo(() => {
    return cn("p-6", containerStyleOverrides)
  }, [containerStyleOverrides])

  const titleContainerClassName = useMemo(() => {
    return cn("flex justify-between items-center pb-6", titleStyleOverrides)
  }, [titleStyleOverrides])

  useEffect(() => {
    setIsDrawerOpen(isOpen)
  }, [isOpen])

  useEffect(() => {
    if (isDrawerOpen) {
      drawerRef.current?.focus()
    }
  }, [isDrawerOpen])

  const handleDrawerClose = useCallback(() => {
    if (onClose) {
      onClose()
    } else {
      setIsDrawerOpen(false)
    }
  }, [onClose, setIsDrawerOpen])

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        closeOnBackdropClick &&
        isDrawerOpen &&
        drawerRef.current &&
        !drawerRef.current.contains(event.target as Node)
      ) {
        // Check if the element or any parent has an ID containing "modal"
        const hasModalId = (element: Element | null): boolean => {
          if (!element) return false
          if (element.id && element.id.toLowerCase().includes("modal"))
            return true
          return element.parentElement
            ? hasModalId(element.parentElement)
            : false
        }

        // Only close if the click is not on a modal
        if (!hasModalId(event.target as Element)) {
          handleDrawerClose()
        }
      }
    }
    if (isDrawerOpen) {
      document.addEventListener("mousedown", handleOutsideClick)
    } else {
      document.removeEventListener("mousedown", handleOutsideClick)
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick)
    }
  }, [isDrawerOpen, handleDrawerClose, closeOnBackdropClick])

  const getDrawerClass = () => {
    const baseClass =
      "fixed bg-white shadow-xl transform transition-transform duration-300 ease-in-out"
    const openClass = "translate-x-0 translate-y-0"
    const closedClassMap: Record<typeof position, string> = {
      left: "-translate-x-full",
      right: "translate-x-full",
      top: "-translate-y-full",
      bottom: "translate-y-full",
    }

    return `${baseClass} ${isDrawerOpen ? openClass : closedClassMap[position]}`
  }

  const getDrawerStyle = () => {
    const defaultStyles: Record<typeof position, React.CSSProperties> = {
      left: {
        top: 0,
        left: 0,
        height: height || "100vh",
        width: width || "50%",
      },
      right: {
        top: 0,
        right: 0,
        height: height || "100vh",
        width: width || "50%",
      },
      top: {
        top: 0,
        left: 0,
        width: width || "100%",
        height: height || "50vh",
      },
      bottom: {
        bottom: 0,
        left: 0,
        width: width || "100%",
        height: height || "50vh",
      },
    }

    return { ...defaultStyles[position], zIndex }
  }

  return (
    <div
      className={`fixed inset-0 ${isDrawerOpen ? "block" : "hidden"}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby={drawerId}
      style={{ zIndex }}
    >
      <div
        className="fixed inset-0 p-6"
        aria-hidden="true"
        style={{
          zIndex: zIndex - 1,
          backgroundColor:
            backdropOpacity !== undefined
              ? `rgba(0, 0, 0, ${backdropOpacity})`
              : "transparent",
        }}
      />
      <div
        ref={drawerRef}
        className={`${getDrawerClass()} ${containerClassName}`}
        style={getDrawerStyle()}
        aria-labelledby={drawerId}
        role="document"
        tabIndex={-1}
      >
        <div className={titleContainerClassName}>
          <div id={drawerId}>
            <Typography
              variant={typographyTypes.PANEL_GROUP_TITLE}
              text={title}
              styleOverrides={"font-medium text-lg"}
            />
          </div>
          <div
            className="cursor-pointer"
            onClick={handleDrawerClose}
            role="button"
            tabIndex={0}
          >
            <Icon iconType="x" size="md" />
          </div>
        </div>
        <div>{children}</div>
      </div>
    </div>
  )
}

export default Drawer
